import React, { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  sendTestNotification,
  registerServiceWorker
} from '../services/pushNotifications';

export default function NotificationSettings() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    const isSupported = isPushSupported();
    setSupported(isSupported);
    
    if (isSupported) {
      setPermission(getNotificationPermission());
      await registerServiceWorker();
      const isSub = await isSubscribedToPush();
      setSubscribed(isSub);
    }
    setLoading(false);
  };

  const handleSubscribe = async () => {
    setLoading(true);
    const result = await subscribeToPush();
    
    if (result.success) {
      setSubscribed(true);
      setPermission('granted');
      toast.success('Push notifications enabled!');
    } else {
      toast.error(result.error || 'Failed to enable notifications');
      if (result.permission === 'denied') {
        setPermission('denied');
      }
    }
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    const result = await unsubscribeFromPush();
    
    if (result.success) {
      setSubscribed(false);
      toast.success('Push notifications disabled');
    } else {
      toast.error('Failed to disable notifications');
    }
    setLoading(false);
  };

  const handleTestNotification = async () => {
    setTesting(true);
    const success = await sendTestNotification();
    
    if (success) {
      toast.success('Test notification sent! Check your device.');
    } else {
      toast.error('Failed to send test notification');
    }
    setTesting(false);
  };

  if (!supported) {
    return (
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="push-unsupported">
        <div className="flex items-center gap-3">
          <BellOff className="h-5 w-5 text-slate-400" />
          <div>
            <p className="font-medium text-slate-700">Push Notifications</p>
            <p className="text-sm text-slate-500">Not supported in this browser</p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200" data-testid="push-denied">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <div>
            <p className="font-medium text-red-700">Notifications Blocked</p>
            <p className="text-sm text-red-600">
              Please enable notifications in your browser settings to receive alerts.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg border border-slate-200" data-testid="push-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {subscribed ? (
            <div className="p-2 bg-green-100 rounded-lg">
              <Bell className="h-5 w-5 text-green-600" />
            </div>
          ) : (
            <div className="p-2 bg-slate-100 rounded-lg">
              <BellOff className="h-5 w-5 text-slate-500" />
            </div>
          )}
          <div>
            <p className="font-medium text-slate-900">Push Notifications</p>
            <p className="text-sm text-slate-500">
              {subscribed 
                ? 'You will receive instant alerts for new messages' 
                : 'Get notified when you receive new messages'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {subscribed && (
            <button
              onClick={handleTestNotification}
              disabled={testing || loading}
              className="btn-secondary text-sm flex items-center gap-1"
              data-testid="test-notification-btn"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Test'
              )}
            </button>
          )}
          
          <button
            onClick={subscribed ? handleUnsubscribe : handleSubscribe}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
              subscribed
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
            data-testid="toggle-push-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : subscribed ? (
              <>
                <BellOff className="h-4 w-4" />
                Disable
              </>
            ) : (
              <>
                <Bell className="h-4 w-4" />
                Enable
              </>
            )}
          </button>
        </div>
      </div>
      
      {subscribed && (
        <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
          <CheckCircle className="h-4 w-4" />
          <span>Notifications are enabled</span>
        </div>
      )}
    </div>
  );
}
